import { computed, onBeforeUnmount, onMounted } from 'vue'

export type BuilderLayoutMode = 'sheet' | 'split'

/**
 * Builder chrome layout:
 * - sheet: phones, or tablets in portrait (max-width 1023px)
 * - split: tablet landscape and desktop
 *
 * Shared via useState so every caller (page + canvas) sees the same mode
 * after the first client measurement. Defaults to `split` for SSR.
 */
export function useBuilderLayout() {
  const layoutMode = useState<BuilderLayoutMode>('builder-layout-mode', () => 'split')
  const isCompactSplit = useState('builder-layout-compact-split', () => false)
  const isCoarsePointer = useState('builder-layout-coarse', () => false)

  function update() {
    if (!import.meta.client) return
    const narrowPhone = window.matchMedia('(max-width: 767px)').matches
    const tabletOrBelow = window.matchMedia('(max-width: 1023px)').matches
    const portrait = window.matchMedia('(orientation: portrait)').matches
    const sheet = narrowPhone || (tabletOrBelow && portrait)
    layoutMode.value = sheet ? 'sheet' : 'split'
    isCompactSplit.value = !sheet && tabletOrBelow
    isCoarsePointer.value = window.matchMedia('(pointer: coarse)').matches
  }

  onMounted(() => {
    update()
    if (!import.meta.client) return
    const mqs = [
      window.matchMedia('(max-width: 767px)'),
      window.matchMedia('(max-width: 1023px)'),
      window.matchMedia('(orientation: portrait)'),
      window.matchMedia('(pointer: coarse)'),
    ]
    mqs.forEach((mq) => mq.addEventListener('change', update))
    onBeforeUnmount(() => {
      mqs.forEach((mq) => mq.removeEventListener('change', update))
    })
  })

  return {
    layoutMode,
    isSheet: computed(() => layoutMode.value === 'sheet'),
    isCompactSplit,
    isCoarsePointer,
  }
}
